import React, { useCallback, useMemo, useRef, useState } from 'react';
import { APPENDIX_TOKENS, mmToPt } from '../../../appendixTokens';
import { Block, PlanAppendixBlockData, PlanMarker, PlanPage, normalisePlan, photoNumbers, planPages } from '../../../config';
import { clampMarkerToBox, fitPlanBox, markerGeometry, markerLabel } from '../../../utils/planMarkers';
import LiveText from '../../LiveText';
import '../appendix-pages.scss';
import './plan-appendix-page.scss';

/**
 * Height of the title block above the plan, in points: one line of the title
 * plus its frame and the gap under it. The plan box is what is left of the
 * content area, on both surfaces.
 */
export const PLAN_TITLE_BLOCK_PT =
  APPENDIX_TOKENS.titleSizePt * APPENDIX_TOKENS.titleLineHeight
  + APPENDIX_TOKENS.titlePadPt * 2
  + APPENDIX_TOKENS.titleGapPt;

export const PLAN_BOX_WIDTH_MM = APPENDIX_TOKENS.contentWidthMm;
export const PLAN_BOX_HEIGHT_MM = APPENDIX_TOKENS.contentHeightMm - PLAN_TITLE_BLOCK_PT / 2.83465;

interface PlanAppendixPageProps {
  block: Block;
  blocks: Block[];
  /** photoNumbers(blocks), when the caller has already worked it out. */
  numbers?: Map<string, number>;
  isEditing: boolean;
  /** Which page of the appendix this is; each page is one plan. */
  pageIndex: number;
  /** Signed URL of this page's plan, when there is one. */
  imageUrl: string | null;
  uploading?: boolean;
  /**
   * A bitmap operation (upload, rotate, trim) is replacing this page's image.
   * The pins are locked meanwhile: a drag that straddled the swap would take
   * its start from the old image and its end on the new one.
   */
  locked?: boolean;
  onChange?: (data: PlanAppendixBlockData) => void;
  onUploadRequest?: () => void;
}

interface DragState {
  markerId: string;
  mode: 'move' | 'rotate';
  startX: number;
  startY: number;
  baseX: number;
  baseY: number;
  /** Screen px per point of the SVG's coordinate space. */
  pxPerPt: number;
}

/**
 * The location plan page: the title, the plan image fitted under it, and the
 * numbered markers over it.
 *
 * The markers are an SVG whose coordinate space is POINTS of the fitted image
 * box, drawn from the geometry planMarkers.ts computes in mm -- the same
 * numbers DocumentPDF hands to react-pdf's Svg. Rotation is applied inside
 * that helper (react-pdf ignores an SVG transform), so both surfaces draw an
 * already-rotated polygon. Dragging writes the marker's normalised x/y and
 * degrees back through onChange; while a drag is in progress the live
 * position is held locally so the page does not wait on the autosave.
 */
const PlanAppendixPage: React.FC<PlanAppendixPageProps> = ({
  block,
  blocks,
  numbers: numbersProp,
  isEditing,
  pageIndex,
  imageUrl,
  uploading = false,
  locked = false,
  onChange,
  onUploadRequest,
}) => {
  const data = block.data as PlanAppendixBlockData;
  const pages = planPages(data);
  const page: PlanPage = pages[pageIndex] ?? pages[0];
  const box = fitPlanBox(page.naturalWidth, page.naturalHeight, PLAN_BOX_WIDTH_MM, PLAN_BOX_HEIGHT_MM, page.align, page.valign);
  const boxWPt = mmToPt(box.widthMm);
  const boxHPt = mmToPt(box.heightMm);
  // The marker layer covers the whole plan box, not just the image, so a pin
  // can be put in the white beside a narrow plan.
  const pageWPt = mmToPt(PLAN_BOX_WIDTH_MM);
  const pageHPt = mmToPt(PLAN_BOX_HEIGHT_MM);
  const numbers = useMemo(() => numbersProp ?? photoNumbers(blocks), [numbersProp, blocks]);

  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [live, setLive] = useState<PlanMarker | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const markers = page.markers.map(m => (live && live.id === m.id ? live : m));

  // Every write replaces this page inside the canonical pages array.
  const updatePage = useCallback((patch: Partial<PlanPage>, extra: Partial<PlanAppendixBlockData> = {}) => {
    const canonical = normalisePlan(data);
    onChange?.({
      ...canonical,
      ...extra,
      pages: (canonical.pages ?? []).map(p => (p.id === page.id ? { ...p, ...patch } : p)),
    });
  }, [data, page.id, onChange]);

  const commit = useCallback((marker: PlanMarker) => {
    updatePage({ markers: page.markers.map(m => (m.id === marker.id ? marker : m)) });
  }, [page.markers, updatePage]);

  const pointerPt = (e: React.PointerEvent): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const scale = pageWPt / rect.width;
    return { x: (e.clientX - rect.left) * scale, y: (e.clientY - rect.top) * scale };
  };

  const startDrag = (e: React.PointerEvent<SVGElement>, marker: PlanMarker, mode: DragState['mode']) => {
    if (!isEditing || locked) return;
    e.preventDefault();
    e.stopPropagation();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    dragRef.current = {
      markerId: marker.id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      baseX: marker.x,
      baseY: marker.y,
      pxPerPt: rect.width / pageWPt,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelectedId(marker.id);
    setLive(marker);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const marker = page.markers.find(m => m.id === drag.markerId);
    if (!marker) return;
    if (drag.mode === 'move') {
      // Fractions of the IMAGE (so the pin follows the drawing), clamped to
      // the page box rather than to the image.
      const dx = (e.clientX - drag.startX) / drag.pxPerPt / boxWPt;
      const dy = (e.clientY - drag.startY) / drag.pxPerPt / boxHPt;
      setLive({
        ...marker,
        ...clampMarkerToBox(drag.baseX + dx, drag.baseY + dy, box, PLAN_BOX_WIDTH_MM, PLAN_BOX_HEIGHT_MM),
      });
    } else {
      const p = pointerPt(e);
      if (!p) return;
      const cx = mmToPt(box.xMm) + boxWPt * marker.x;
      const cy = mmToPt(box.yMm) + boxHPt * marker.y;
      // 0 = arrow up, clockwise positive: the angle of the pointer from the
      // circle's centre, measured from straight up.
      const deg = (Math.atan2(p.x - cx, -(p.y - cy)) * 180) / Math.PI;
      setLive({ ...marker, rotation: Math.round(deg) });
    }
  };

  const endDrag = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && live && live.id === drag.markerId) commit(live);
    setLive(null);
  };

  const strokePt = mmToPt(APPENDIX_TOKENS.markerStrokeMm);
  const labelSize = APPENDIX_TOKENS.markerLabelSizePt;

  return (
    <div className="apx-content">
      {/* One line, always: the plan box below is sized for exactly one
          (PLAN_BOX_HEIGHT_MM), so a title that wrapped would push the plan
          into the footer margin on both surfaces. A longer one is cut with an
          ellipsis, here and in the PDF. */}
      <LiveText
        as="h2"
        className="apx-title apx-title--single-line"
        value={(pageIndex === 0 ? (page.title ?? data.title) : page.title) || ''}
        editable={isEditing}
        restoreKey={`${block.id}:${page.id}:title`}
        data-placeholder={pageIndex === 0 ? 'Appendix Title...' : 'Page title (e.g. Level 2)...'}
        onCommit={(title) => {
          // The first page's line IS the appendix title (the rail names the
          // appendix by it); later pages carry their own, e.g. a level.
          updatePage({ title }, pageIndex === 0 ? { title } : {});
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      />

      <div
        className="plan-appendix"
        style={{ width: `${PLAN_BOX_WIDTH_MM}mm`, height: `${PLAN_BOX_HEIGHT_MM}mm` }}
        onClick={() => setSelectedId(null)}
      >
        {page.storagePath && imageUrl ? (
          <div
            className="plan-appendix__stage"
            style={{ left: `${box.xMm}mm`, top: `${box.yMm}mm`, width: `${box.widthMm}mm`, height: `${box.heightMm}mm` }}
          >
            <img src={imageUrl} alt={page.title || data.title || 'Location plan'} draggable={false} />
          </div>
        ) : isEditing ? (
          <div className="plan-appendix__dropzone plan-appendix__toolbar">
            <div className="plan-appendix__dropzone-title">
              {uploading ? 'Uploading plan…' : 'Add the location plan'}
            </div>
            <div className="plan-appendix__dropzone-hint">
              An image or a PDF page of the plan. Markers are placed on it from the Place menu on the bar above.
            </div>
            <button
              type="button"
              className="plan-appendix__upload"
              onClick={(e) => { e.stopPropagation(); onUploadRequest?.(); }}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : 'Choose file'}
            </button>
          </div>
        ) : null}
        {page.storagePath && imageUrl && (
            <svg
              ref={svgRef}
              className="plan-appendix__markers"
              viewBox={`0 0 ${pageWPt} ${pageHPt}`}
              width="100%"
              height="100%"
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {markers.map(marker => {
                const g = markerGeometry(marker, box);
                const cx = mmToPt(g.cx);
                const cy = mmToPt(g.cy);
                const r = mmToPt(g.r);
                const points = g.arrowPoints
                  .split(' ')
                  .map(pair => pair.split(',').map(v => mmToPt(Number(v)).toFixed(3)).join(','))
                  .join(' ');
                const label = markerLabel(marker, data, blocks, numbers);
                const selected = isEditing && selectedId === marker.id;
                return (
                  <g
                    key={marker.id}
                    className={`plan-marker${selected ? ' is-selected' : ''}${isEditing ? ' is-editable' : ''}`}
                    onPointerDown={(e) => startDrag(e, marker, 'move')}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <polygon
                      points={points}
                      fill={APPENDIX_TOKENS.markerStroke}
                      stroke={APPENDIX_TOKENS.markerStroke}
                      strokeWidth={strokePt}
                      strokeLinejoin="round"
                    />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={APPENDIX_TOKENS.markerFill}
                      stroke={APPENDIX_TOKENS.markerStroke}
                      strokeWidth={strokePt}
                    />
                    <text
                      x={cx}
                      // Centre the digits on the circle: cap height is ~0.7 of
                      // the size in Tinos, so the baseline sits 0.35 below.
                      y={cy + labelSize * 0.35}
                      fontSize={labelSize}
                      fontWeight="bold"
                      textAnchor="middle"
                      fill={APPENDIX_TOKENS.markerInk}
                    >
                      {label}
                    </text>
                    {selected && (
                      <circle
                        className="plan-appendix__handle"
                        cx={mmToPt(g.tipX)}
                        cy={mmToPt(g.tipY)}
                        r={r * 0.55}
                        onPointerDown={(e) => startDrag(e, marker, 'rotate')}
                      />
                    )}
                  </g>
                );
              })}
            </svg>
        )}
      </div>
    </div>
  );
};

export default PlanAppendixPage;
